import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getTasks, createTask, updateTask, deleteTask } from '../services/api';
import './Tasks.css';

const priorityColor = p => p === 'High' ? '#ff4d4d' : p === 'Medium' ? '#f5a623' : '#A7F432';
const priorityBadge = p => p === 'High' ? 'badge badge-red' : p === 'Medium' ? 'badge badge-amber' : 'badge badge-green';
const FILTERS = ['All', 'Due today', 'Overdue', 'Completed'];

export default function Tasks({ toast }) {
  const { token } = useApp();
  const [tasks, setTasks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('All');
  const [search, setSearch]     = useState('');
  const [creating, setCreating] = useState(false);
  const [newTask, setNewTask]   = useState({ title:'', contact:'', priority:'Medium', due:'', notes:'' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await getTasks(token);
      setTasks(res.data || []);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]);

  const toggle = async (task) => {
    try {
      await updateTask(token, task.id, { status: task.status === 'done' ? 'open' : 'done' });
      load();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const handleCreate = async () => {
    if (!newTask.title.trim()) { toast?.('Enter a task title', 'error'); return; }
    try {
      await createTask(token, { ...newTask, status: 'open' });
      toast?.('Task created');
      setCreating(false);
      setNewTask({ title:'', contact:'', priority:'Medium', due:'', notes:'' });
      load();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTask(token, id);
      toast?.('Task deleted');
      load();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const filtered = tasks.filter(t => {
    if (filter === 'Completed' && t.status !== 'done') return false;
    if (filter === 'Due today' && t.due !== new Date().toISOString().split('T')[0]) return false;
    if (search && !t.title?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const done    = tasks.filter(t => t.status === 'done').length;
  const open    = tasks.filter(t => t.status !== 'done').length;
  const high    = tasks.filter(t => t.priority === 'High' && t.status !== 'done').length;

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total tasks</div><div className="stat-value">{tasks.length}</div><div className="stat-sub">All tasks</div></div>
        <div className="stat-card"><div className="stat-label">Open</div><div className="stat-value">{open}</div><div className="stat-sub">Pending</div></div>
        <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value">{done}</div><div className="stat-sub stat-up">Done</div></div>
        <div className="stat-card"><div className="stat-label">High priority</div><div className="stat-value">{high}</div><div className="stat-sub" style={{color:'#ff4d4d'}}>Urgent</div></div>
      </div>

      <div className="filter-pills" style={{ marginBottom:12 }}>
        {FILTERS.map(f => <button key={f} className={`pill ${filter===f?'active':''}`} onClick={() => setFilter(f)}>{f}</button>)}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <input className="search-input" placeholder="Search tasks…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex:1 }} />
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New task</button>
      </div>

      {creating && (
        <div className="card" style={{ marginBottom:16, padding:'16px 20px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
            <input className="search-input" placeholder="Task title *" value={newTask.title} onChange={e => setNewTask(p=>({...p,title:e.target.value}))} />
            <input className="search-input" placeholder="Contact name" value={newTask.contact} onChange={e => setNewTask(p=>({...p,contact:e.target.value}))} />
            <select value={newTask.priority} onChange={e => setNewTask(p=>({...p,priority:e.target.value}))}
              style={{ background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }}>
              <option>High</option><option>Medium</option><option>Low</option>
            </select>
            <input type="date" value={newTask.due} onChange={e => setNewTask(p=>({...p,due:e.target.value}))}
              style={{ background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }} />
          </div>
          <textarea placeholder="Notes (optional)" value={newTask.notes} onChange={e => setNewTask(p=>({...p,notes:e.target.value}))} rows={2}
            style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px', marginBottom:10, resize:'none' }} />
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-primary" onClick={handleCreate}>Create task</button>
            <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading tasks…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>No tasks found</div>
      ) : (
        <div className="card" style={{ padding:0 }}>
          {filtered.map((t, i) => (
            <div key={t.id} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'14px 20px', borderBottom: i < filtered.length-1 ? '1px solid var(--border)' : 'none' }}>
              <input type="checkbox" checked={t.status === 'done'} onChange={() => toggle(t)}
                style={{ marginTop:3, accentColor:'var(--accent)', width:16, height:16, cursor:'pointer', flexShrink:0 }} />
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:13, fontWeight:500, color: t.status==='done' ? 'var(--text-dim)' : 'var(--text-primary)', textDecoration: t.status==='done' ? 'line-through' : 'none' }}>{t.title}</span>
                  <span className={priorityBadge(t.priority)} style={{ fontSize:10 }}>{t.priority}</span>
                </div>
                <div style={{ fontSize:11, color:'var(--text-dim)' }}>
                  {t.contact && <span>{t.contact} · </span>}
                  {t.due && <span>Due: {new Date(t.due).toLocaleDateString()}</span>}
                  {t.notes && <span> · {t.notes}</span>}
                </div>
              </div>
              <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 8px', color:'var(--danger)' }} onClick={() => handleDelete(t.id)}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
